import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import {
  FIT_TABLE_CONTAINER_SX,
  FIT_TABLE_SX,
  FitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

/** Fixed % columns (Bookings / VAT fit-table pattern). Guest is compact so Booking, Payout TX, Discrepancy, and Actions can fit. */
const NIALL_STAY_COL_PCT = {
  uid: 4,
  dates: 7.5,
  guest: 12,
  guests: 4,
  platform: 4.5,
  payment: 6,
  cleaning: 5,
  net: 5.5,
  commission: 6.5,
  extra: 5,
  fee: 5.5,
  vat: 4.5,
  booking: 9,
  payout: 9,
  discrepancy: 8,
  actions: 4,
} as const;

const DETAIL_PANEL_WIDTH_PX = 440;

type LinkStatus = 'unmatched' | 'suggested' | 'confirmed' | 'declined';

/** Split "Alrick (12)", "Bernadette (6+1)", or "Jennifer (7) - VRBO" into name and party counts. */
export function parseGuestParty(
  raw: string | null | undefined,
  platform?: string | null
): {
  name: string;
  guests: number | null;
  adults: number | null;
  children: number | null;
} {
  let text = (raw || '').trim();
  if (!text) return { name: '', guests: null, adults: null, children: null };
  const stripVrbo =
    (platform || '').trim().toLowerCase() === 'vrbo' || /\bVRBO\b/i.test(text);
  if (stripVrbo) {
    text = text.replace(/\bVRBO\b/gi, '');
    text = text.replace(/\(\s*\)/g, '');
    text = text.replace(/\s{2,}/g, ' ').trim();
    text = text.replace(/\s*[-–—/]+\s*$/g, '').trim();
  }
  if (!text) return { name: '', guests: null, adults: null, children: null };
  const countMatch = text.match(/^(.*?)\s*\((\d+(?:\s*\+\s*\d+)*)\)\s*$/);
  if (countMatch) {
    const parts = countMatch[2].split(/\s*\+\s*/).map((n) => parseInt(n, 10));
    const adults = parts[0];
    const children = parts.length > 1 ? parts.slice(1).reduce((sum, n) => sum + n, 0) : null;
    const guests = parts.reduce((sum, n) => sum + n, 0);
    return {
      name: (countMatch[1] || '').trim() || text,
      guests,
      adults,
      children,
    };
  }
  return { name: text, guests: null, adults: null, children: null };
}

/** Adults / children from Niall’s invoice only — never overwritten by a linked booking. */
export function stayPartyCounts(party: {
  guests: number | null;
  adults?: number | null;
  children?: number | null;
}): { adults: number | null; children: number | null } {
  if (party.children != null) {
    return { adults: party.adults ?? null, children: party.children };
  }
  return { adults: party.adults ?? party.guests, children: null };
}

export function formatGuestCounts(counts: { adults: number | null; children: number | null }): string {
  if (counts.adults == null && counts.children == null) return '—';
  const adults = counts.adults == null ? '—' : String(counts.adults);
  const children = counts.children == null ? '—' : String(counts.children);
  return `${adults} / ${children}`;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayOrdinal(day: number): string {
  const v = day % 100;
  if (v >= 11 && v <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/** Display stay dates with a 3-letter month, e.g. "5th - 8th Dec" or "28th Dec - 3rd Jan". */
export function formatStayDates(
  datesRaw: string | null | undefined,
  checkIn: string | null | undefined,
  checkOut: string | null | undefined,
  fallbackMonth?: number
): string {
  if (checkIn && checkOut) {
    const inn = new Date(`${checkIn}T00:00:00`);
    const out = new Date(`${checkOut}T00:00:00`);
    if (!Number.isNaN(inn.getTime()) && !Number.isNaN(out.getTime())) {
      const inM = SHORT_MONTHS[inn.getMonth()];
      const outM = SHORT_MONTHS[out.getMonth()];
      const inD = dayOrdinal(inn.getDate());
      const outD = dayOrdinal(out.getDate());
      if (inn.getMonth() === out.getMonth() && inn.getFullYear() === out.getFullYear()) {
        return `${inD} - ${outD} ${inM}`;
      }
      return `${inD} ${inM} - ${outD} ${outM}`;
    }
  }
  const raw = (datesRaw || '').trim();
  if (!raw) return '—';
  if (/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/i.test(raw)) {
    return raw;
  }
  const mon =
    fallbackMonth && fallbackMonth >= 1 && fallbackMonth <= 12 ? SHORT_MONTHS[fallbackMonth - 1] : '';
  return mon ? `${raw} ${mon}` : raw;
}

const AMOUNT_EPS = 0.02;

function firstNameToken(name: string | null | undefined): string {
  return (name || '').trim().split(/\s+/)[0]?.toLowerCase() || '';
}

function normPlatform(value?: string | null): string {
  const text = (value || '').trim().toUpperCase();
  if (!text) return '';
  if (text.startsWith('AIRBNB')) return 'AIRBNB';
  if (text.startsWith('VRBO') || text.startsWith('HOMEAWAY')) return 'VRBO';
  if (text.startsWith('BOOKING')) return 'BOOKING';
  return text;
}

function isActiveLink(status?: LinkStatus | null, id?: number | null): boolean {
  return Boolean(id) && status !== 'unmatched' && status !== 'declined';
}

function amountsDiffer(a?: number | null, b?: number | null): boolean {
  if (a == null || b == null) return false;
  return Math.abs(Number(a) - Math.abs(Number(b))) > AMOUNT_EPS;
}

function euroLabel(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `€${Number(n).toFixed(2)}`;
}

export interface StayDiscrepancy {
  field: string;
  detail: string;
}

export interface PayoutShareMember {
  booking_id: number;
  stay_id?: number | null;
  guest_name?: string | null;
  property_code?: string | null;
  platform?: string | null;
  check_in_date?: string | null;
  check_out_date?: string | null;
  amount?: number | null;
  is_current?: boolean;
}

export interface PayoutShare {
  tx_id: number;
  tx_amount: number | null;
  stay_amount: number | null;
  members: PayoutShareMember[];
}

export function payoutShareIsBalanced(share?: PayoutShare | null): boolean {
  if (!share?.members || share.members.length < 2 || share.tx_amount == null) return false;
  const sum = share.members.reduce((acc, m) => acc + Math.abs(Number(m.amount || 0)), 0);
  return Math.abs(sum - Math.abs(Number(share.tx_amount))) <= AMOUNT_EPS;
}

function payoutShareTooltip(share: PayoutShare): string {
  const others = share.members.filter((m) => !m.is_current);
  const bits = others.map((m) => {
    const name = (m.guest_name || 'Guest').trim().split(/\s+/)[0];
    return m.property_code ? `${name} (${m.property_code})` : name;
  });
  const withPart = bits.length ? ` with ${bits.join(', ')}` : '';
  return `${euroLabel(share.stay_amount)} of ${euroLabel(share.tx_amount)}${withPart}`;
}

function payoutShareTotalLine(share: PayoutShare): string {
  const parts = share.members.map((m) => euroLabel(m.amount));
  return `${parts.join(' + ')} = ${euroLabel(share.tx_amount)}`;
}

/** Differences between Niall’s original invoice line and a linked booking / payout TX. */
export function stayLinkDiscrepancies(
  stay: {
    dates_raw?: string | null;
    check_in_date?: string | null;
    check_out_date?: string | null;
    platform?: string | null;
    total_payment?: number | null;
    unified_booking_id?: number | null;
    booking_link_status?: LinkStatus;
    booking?: {
      guest_name?: string | null;
      platform?: string | null;
      check_in_date?: string | null;
      check_out_date?: string | null;
      adults_count?: number | null;
      children_count?: number | null;
      host_payout_amount?: number | null;
      total_payout_amount?: number | null;
    } | null;
    payout_tx_id?: number | null;
    payout_tx_link_status?: LinkStatus;
    payout_tx?: { amount?: number | null } | null;
    payout_share?: PayoutShare | null;
  },
  party: { name: string; guests: number | null; adults: number | null; children: number | null },
  fallbackMonth?: number
): StayDiscrepancy[] {
  const diffs: StayDiscrepancy[] = [];
  const booking = stay.booking;
  if (booking && isActiveLink(stay.booking_link_status, stay.unified_booking_id)) {
    const invoiceDates = formatStayDates(stay.dates_raw, stay.check_in_date, stay.check_out_date, fallbackMonth);
    const bookingDates = formatStayDates(null, booking.check_in_date, booking.check_out_date, fallbackMonth);
    const inDiff = stay.check_in_date && booking.check_in_date && stay.check_in_date !== booking.check_in_date;
    const outDiff = stay.check_out_date && booking.check_out_date && stay.check_out_date !== booking.check_out_date;
    if (inDiff || outDiff) {
      diffs.push({ field: 'Dates', detail: `Invoice ${invoiceDates} · Booking ${bookingDates}` });
    }

    const invoiceToken = firstNameToken(party.name);
    const bookingToken = firstNameToken(booking.guest_name);
    if (invoiceToken && bookingToken && invoiceToken !== bookingToken) {
      diffs.push({
        field: 'Guest',
        detail: `Invoice ${party.name || '—'} · Booking ${booking.guest_name || '—'}`,
      });
    }

    const invoicePlat = normPlatform(stay.platform);
    const bookingPlat = normPlatform(booking.platform);
    if (invoicePlat && bookingPlat && invoicePlat !== bookingPlat) {
      diffs.push({
        field: 'Platform',
        detail: `Invoice ${stay.platform} · Booking ${booking.platform}`,
      });
    }

    const bAdults = booking.adults_count;
    const bChildren = booking.children_count;
    const bookingHasGuests = (bAdults != null && bAdults !== 0) || (bChildren != null && bChildren !== 0);
    const invoiceHasGuests = party.adults != null || party.guests != null || party.children != null;
    if (bookingHasGuests && invoiceHasGuests) {
      const invoiceCounts = stayPartyCounts(party);
      const bookingCounts = { adults: bAdults ?? 0, children: bChildren ?? 0 };
      let guestsDiffer = false;
      if (party.children != null) {
        guestsDiffer =
          (party.adults ?? 0) !== bookingCounts.adults || party.children !== bookingCounts.children;
      } else {
        const invoiceTotal = party.guests ?? party.adults;
        if (invoiceTotal != null) {
          guestsDiffer =
            bookingCounts.adults + bookingCounts.children !== invoiceTotal || bookingCounts.children > 0;
        }
      }
      if (guestsDiffer) {
        diffs.push({
          field: 'Guests',
          detail: `Invoice ${formatGuestCounts(invoiceCounts)} · Booking ${formatGuestCounts(bookingCounts)}`,
        });
      }
    }

    const bookingAmt = booking.host_payout_amount ?? booking.total_payout_amount;
    if (amountsDiffer(stay.total_payment, bookingAmt)) {
      diffs.push({
        field: 'Payment',
        detail: `Invoice ${euroLabel(stay.total_payment)} · Booking ${euroLabel(bookingAmt)}`,
      });
    }
  }

  const payout = stay.payout_tx;
  if (payout && isActiveLink(stay.payout_tx_link_status, stay.payout_tx_id)) {
    if (amountsDiffer(stay.total_payment, payout.amount) && !payoutShareIsBalanced(stay.payout_share)) {
      diffs.push({
        field: 'Payout TX',
        detail: `Invoice ${euroLabel(stay.total_payment)} · TX ${euroLabel(
          payout.amount == null ? null : Math.abs(Number(payout.amount))
        )}`,
      });
    }
  }

  return diffs;
}

const COLUMN_TIPS: Record<string, string> = {
  uid: 'Internal stay id (niall_calc_stays).',
  dates: 'Stay dates from the invoice workbook.',
  guest: 'Guest name. Extra notes from the workbook appear under the name.',
  guests:
    'Adults / children from Niall’s invoice: (6+1) or a combined total such as (12). Linked booking counts are not used here; mismatches appear in Discrepancy.',
  platform: 'Booking platform (Airbnb, Booking.com, etc.).',
  payment: 'Gross payment received for this stay.',
  cleaning: 'Cleaning fee for this stay.',
  net: 'Total payment minus cleaning.',
  commission: 'Minus cleaning × 10% or 19%, using the rate on that invoice line.',
  extra: 'Additional amount added to Niall’s fee (usually extra cleaning). Hover for the invoice note.',
  fee: 'Cleaning + Commission + Additional Fees.',
  vat: 'Total fee × 13.5%.',
  booking: 'Linked unified booking for this stay.',
  payout: 'Linked stay payout transaction.',
  discrepancy:
    'Differences between Niall’s original invoice line (this page) and the linked booking or payout TX. Invoice figures are not changed.',
  actions: 'Open a side panel with the full invoice line and any linked booking or payout TX. Clicking the row does the same, except in Booking, Payout TX, and Discrepancy.',
};

const MONTH_TIPS = {
  cleaning_total: 'Sum of cleaning fees on this invoice.',
  total_due: 'Sum of property Total Fees (Niall’s fee before VAT).',
  vat_total: 'Sum of stay VAT.',
  total_incl_vat: 'Total due + VAT.',
};

const HeaderTip: React.FC<{ label: string; tip: string; variant?: 'column' | 'inline' }> = ({
  label,
  tip,
  variant = 'inline',
}) => (
  <Tooltip title={tip}>
    <Box
      component="span"
      tabIndex={0}
      sx={
        variant === 'column'
          ? {
              display: 'block',
              width: '100%',
              textAlign: 'center',
              whiteSpace: 'pre-line',
              overflowWrap: 'break-word',
              lineHeight: 1.15,
              textDecoration: 'underline dotted',
              textUnderlineOffset: '2px',
              cursor: 'help',
            }
          : { borderBottom: '1px dotted', cursor: 'help', whiteSpace: 'nowrap' }
      }
    >
      {label}
    </Box>
  </Tooltip>
);

interface CalcStay {
  id: number;
  property_code: string;
  dates_raw: string;
  check_in_date?: string | null;
  check_out_date?: string | null;
  guest_name: string;
  platform: string | null;
  notes: string | null;
  total_payment: number | null;
  cleaning_fee: number | null;
  total_minus_cleaning: number | null;
  commission: number | null;
  total_fee_extra: number | null;
  total_fee: number | null;
  vat: number | null;
  calc_display: Record<string, string>;
  excel_formulas: Record<string, string | null>;
  unified_booking_id: number | null;
  booking_link_status: LinkStatus;
  booking?: {
    id?: number;
    guest_name?: string | null;
    platform?: string | null;
    source?: string | null;
    check_in_date?: string | null;
    check_out_date?: string | null;
    adults_count?: number | null;
    children_count?: number | null;
    host_payout_amount?: number | null;
    total_payout_amount?: number | null;
    revolut_transaction_db_id?: number | null;
  } | null;
  payout_tx_id: number | null;
  payout_tx_link_status: LinkStatus;
  payout_tx?: {
    id?: number;
    revolut_id?: string | null;
    amount?: number | null;
    currency?: string | null;
    description?: string | null;
    counterparty_name?: string | null;
    completed_at?: string | null;
    created_at_revolut?: string | null;
  } | null;
  payout_share?: PayoutShare | null;
}

interface PropertyGroup {
  property_code: string;
  total_fee: number;
  total_fee_calc: string;
  stays: CalcStay[];
}

interface MonthPayload {
  id: number;
  year: number;
  month: number;
  sheet_name: string;
  cleaning_total: number | null;
  total_due: number | null;
  vat_total: number | null;
  total_incl_vat: number | null;
  summary_formulas: { display?: Record<string, string> };
  fee_tx_id: number | null;
  fee_tx_link_status: LinkStatus;
  stay_count?: number;
  properties?: PropertyGroup[];
}

const money = (n: number | null | undefined) =>
  n == null || Number.isNaN(n)
    ? '—'
    : `€${Number(n).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatIsoStamp = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-IE');
};

const statusColor = (status: LinkStatus | undefined) => {
  if (status === 'confirmed') return 'success' as const;
  if (status === 'suggested') return 'warning' as const;
  if (status === 'declined') return 'default' as const;
  return 'error' as const;
};

const DetailLine: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.4, alignItems: 'flex-start' }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 128, flexShrink: 0, pt: 0.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value == null || value === '' ? '—' : value}
    </Typography>
  </Box>
);

export const NiallCalcs: React.FC = () => {
  const { user } = useAuth();
  const canWrite = user?.role === 'admin' || user?.role === 'owner';
  const [months, setMonths] = useState<MonthPayload[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyKind, setBusyKind] = useState<'import' | 'suggest' | 'link' | null>(null);
  const busy = busyKind != null;
  const busyStatus =
    busyKind === 'suggest'
      ? 'Matching stays to bookings and payout transactions. This can take a minute…'
      : busyKind === 'import'
        ? 'Importing the workbook, then matching stays to bookings and payouts…'
        : busyKind === 'link'
          ? 'Saving the link…'
          : '';
  const [linkDialog, setLinkDialog] = useState<{
    kind: 'booking' | 'payout' | 'fee';
    stayId?: number;
    monthId?: number;
  } | null>(null);
  const [linkId, setLinkId] = useState('');
  const [detailStayId, setDetailStayId] = useState<number | null>(null);

  const detailContext = useMemo(() => {
    if (detailStayId == null) return null;
    for (const month of months) {
      for (const group of month.properties || []) {
        const stay = group.stays.find((row) => row.id === detailStayId);
        if (stay) return { stay, month, propertyCode: group.property_code };
      }
    }
    return null;
  }, [months, detailStayId]);

  useEffect(() => {
    if (detailStayId == null || loading || months.length === 0) return;
    if (!detailContext) setDetailStayId(null);
  }, [detailStayId, detailContext, loading, months.length]);

  const isExpanded = useCallback(
    (id: number) => (expandedIds === null ? true : expandedIds.has(id)),
    [expandedIds]
  );

  const setMonthExpanded = (id: number, open: boolean) => {
    setExpandedIds((prev) => {
      const next = new Set(prev === null ? months.map((row) => row.id) : prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const openAllSections = () => {
    setExpandedIds(new Set(months.map((row) => row.id)));
  };

  const closeAllSections = () => {
    setExpandedIds(new Set());
  };

  const loadMonths = useCallback(async () => {
    const res = await api.get('/api/niall-calcs/months', { params: { details: 1 } });
    let list: MonthPayload[] = res.data.months || [];
    const missing = list.filter((m) => !Array.isArray(m.properties));
    if (missing.length) {
      const extra = await Promise.all(
        missing.map((m) =>
          api.get(`/api/niall-calcs/months/${m.year}/${m.month}`).then((r) => r.data as MonthPayload)
        )
      );
      const byKey = new Map(extra.map((row) => [`${row.year}-${row.month}`, row]));
      list = list.map((m) => byKey.get(`${m.year}-${m.month}`) || m);
    }
    setMonths(list);
    return list;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await loadMonths();
        if (cancelled) return;
        setError('');
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load Niall’s Calcs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMonths]);

  const refreshAll = async () => {
    await loadMonths();
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append('file', file);
      try {
        setBusyKind('import');
        await api.post('/api/niall-calcs/import', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setBusyKind('suggest');
        try {
          await api.post('/api/niall-calcs/suggest-links', {});
        } catch (err: any) {
          setError(err.response?.data?.error || 'Imported, but suggest links failed');
        }
        await refreshAll();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Import failed');
      } finally {
        setBusyKind(null);
      }
    };
    input.click();
  };

  const handleSuggest = async () => {
    try {
      setBusyKind('suggest');
      await api.post('/api/niall-calcs/suggest-links', {});
      await refreshAll();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Suggest links failed');
    } finally {
      setBusyKind(null);
    }
  };

  const patchStayLink = async (stayId: number, kind: 'booking' | 'payout', body: Record<string, unknown>) => {
    const path = kind === 'booking' ? 'booking' : 'payout-tx';
    await api.put(`/api/niall-calcs/stays/${stayId}/${path}`, body);
    await refreshAll();
  };

  const patchFeeLink = async (monthId: number, body: Record<string, unknown>) => {
    await api.put(`/api/niall-calcs/months/${monthId}/fee-tx`, body);
    await refreshAll();
  };

  const submitManualLink = async () => {
    if (!linkDialog) return;
    const id = parseInt(linkId, 10);
    if (!id) {
      setError('Enter a numeric id');
      return;
    }
    try {
      setBusyKind('link');
      if (linkDialog.kind === 'booking' && linkDialog.stayId) {
        await patchStayLink(linkDialog.stayId, 'booking', { unified_booking_id: id, status: 'confirmed' });
      } else if (linkDialog.kind === 'payout' && linkDialog.stayId) {
        await patchStayLink(linkDialog.stayId, 'payout', { payout_tx_id: id, status: 'confirmed' });
      } else if (linkDialog.kind === 'fee' && linkDialog.monthId) {
        await patchFeeLink(linkDialog.monthId, { fee_tx_id: id, status: 'confirmed' });
      }
      setLinkDialog(null);
      setLinkId('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save link');
    } finally {
      setBusyKind(null);
    }
  };

  const iconBtnSx = {
    p: 0.25,
    width: 22,
    height: 22,
    '& .MuiSvgIcon-root': { fontSize: '1rem' },
  };

  const renderLinkChip = (
    label: string,
    status: LinkStatus,
    idValue: number | null,
    handlers: { onConfirm?: () => void; onDecline?: () => void; onPick?: () => void },
    opts?: { hideKind?: boolean; chipLabel?: string; chipTitle?: string }
  ) => {
    const chipLabel =
      opts?.chipLabel ||
      (opts?.hideKind
        ? idValue
          ? `#${idValue}`
          : status
        : idValue
          ? `${label} #${idValue}`
          : `${label}: ${status}`);
    const chip = (
      <Chip
        size="small"
        color={statusColor(status)}
        label={chipLabel}
        sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
      />
    );
    return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 0.125, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
      {opts?.chipTitle ? (
        <Tooltip title={opts.chipTitle}>
          <span>{chip}</span>
        </Tooltip>
      ) : (
        chip
      )}
      {canWrite && (
        <>
          {status === 'suggested' && handlers.onConfirm && (
            <Tooltip title="Confirm">
              <span>
                <IconButton
                  size="small"
                  aria-label="Confirm"
                  onClick={handlers.onConfirm}
                  disabled={busy}
                  sx={iconBtnSx}
                >
                  <CheckIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {(status === 'suggested' || status === 'confirmed') && handlers.onDecline && (
            <Tooltip title="Decline">
              <span>
                <IconButton
                  size="small"
                  aria-label="Decline"
                  onClick={handlers.onDecline}
                  disabled={busy}
                  sx={iconBtnSx}
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {handlers.onPick && (
            <Tooltip title="Pick">
              <span>
                <IconButton
                  size="small"
                  aria-label="Pick"
                  onClick={handlers.onPick}
                  disabled={busy}
                  sx={iconBtnSx}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      )}
    </Box>
    );
  };

  const applyStayLink = async (
    stayId: number,
    kind: 'booking' | 'payout',
    body: Record<string, unknown>
  ) => {
    try {
      setBusyKind('link');
      await patchStayLink(stayId, kind, body);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save link');
    } finally {
      setBusyKind(null);
    }
  };

  const applyFeeLink = async (monthId: number, body: Record<string, unknown>) => {
    try {
      setBusyKind('link');
      await patchFeeLink(monthId, body);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save link');
    } finally {
      setBusyKind(null);
    }
  };

  const renderPanelLinkActions = (
    status: LinkStatus,
    idValue: number | null,
    labels: { confirm: string; reject: string; pick: string },
    handlers: { onConfirm?: () => void; onDecline?: () => void; onPick?: () => void }
  ) => {
    if (!canWrite) return null;
    const showConfirm = status === 'suggested' && Boolean(idValue) && handlers.onConfirm;
    const showReject = (status === 'suggested' || status === 'confirmed') && handlers.onDecline;
    if (!showConfirm && !showReject && !handlers.onPick) return null;
    return (
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
        {showConfirm ? (
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            onClick={handlers.onConfirm}
            disabled={busy}
            aria-label={labels.confirm}
          >
            Confirm
          </Button>
        ) : null}
        {showReject ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handlers.onDecline}
            disabled={busy}
            aria-label={labels.reject}
          >
            Reject
          </Button>
        ) : null}
        {handlers.onPick ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handlers.onPick}
            disabled={busy}
            aria-label={labels.pick}
          >
            Pick
          </Button>
        ) : null}
      </Stack>
    );
  };

  const renderMonthBody = (detail: MonthPayload) => (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            <HeaderTip label="Cleaning total" tip={MONTH_TIPS.cleaning_total} />
          </Typography>
          <Typography>{money(detail.cleaning_total)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            <HeaderTip label="Total due" tip={MONTH_TIPS.total_due} />
          </Typography>
          <Typography>{money(detail.total_due)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            <HeaderTip label="VAT" tip={MONTH_TIPS.vat_total} />
          </Typography>
          <Typography>{money(detail.vat_total)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            <HeaderTip label="Total incl VAT" tip={MONTH_TIPS.total_incl_vat} />
          </Typography>
          <Typography fontWeight={600}>{money(detail.total_incl_vat)}</Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Monthly Niall fee TX
          </Typography>
          {renderLinkChip('Fee TX', detail.fee_tx_link_status || 'unmatched', detail.fee_tx_id, {
            onConfirm: detail.fee_tx_id
              ? () => patchFeeLink(detail.id, { fee_tx_id: detail.fee_tx_id, status: 'confirmed' })
              : undefined,
            onDecline: () => patchFeeLink(detail.id, { status: 'declined' }),
            onPick: () => {
              setLinkDialog({ kind: 'fee', monthId: detail.id });
              setLinkId(detail.fee_tx_id ? String(detail.fee_tx_id) : '');
            },
          })}
        </Box>
      </Box>

      {(detail.properties || []).map((group) => (
        <Box key={group.property_code} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {group.property_code}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              TOTAL {money(group.total_fee)}
            </Typography>
          </Typography>
          <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
            <Table size="small" sx={FIT_TABLE_SX} data-testid="niall-calcs-table">
              <FitColgroup
                cols={[
                  { key: 'uid', pct: NIALL_STAY_COL_PCT.uid },
                  { key: 'dates', pct: NIALL_STAY_COL_PCT.dates },
                  { key: 'guest', pct: NIALL_STAY_COL_PCT.guest },
                  { key: 'guests', pct: NIALL_STAY_COL_PCT.guests },
                  { key: 'platform', pct: NIALL_STAY_COL_PCT.platform },
                  { key: 'payment', pct: NIALL_STAY_COL_PCT.payment },
                  { key: 'cleaning', pct: NIALL_STAY_COL_PCT.cleaning },
                  { key: 'net', pct: NIALL_STAY_COL_PCT.net },
                  { key: 'commission', pct: NIALL_STAY_COL_PCT.commission },
                  { key: 'extra', pct: NIALL_STAY_COL_PCT.extra },
                  { key: 'fee', pct: NIALL_STAY_COL_PCT.fee },
                  { key: 'vat', pct: NIALL_STAY_COL_PCT.vat },
                  { key: 'booking', pct: NIALL_STAY_COL_PCT.booking },
                  { key: 'payout', pct: NIALL_STAY_COL_PCT.payout },
                  { key: 'discrepancy', pct: NIALL_STAY_COL_PCT.discrepancy },
                  { key: 'actions', pct: NIALL_STAY_COL_PCT.actions },
                ]}
              />
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="UID" tip={COLUMN_TIPS.uid} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Dates" tip={COLUMN_TIPS.dates} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Guest" tip={COLUMN_TIPS.guest} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="No. of Guests" tip={COLUMN_TIPS.guests} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Platform" tip={COLUMN_TIPS.platform} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label={'Total\npayment'} tip={COLUMN_TIPS.payment} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Cleaning" tip={COLUMN_TIPS.cleaning} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Minus cleaning" tip={COLUMN_TIPS.net} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Commission" tip={COLUMN_TIPS.commission} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label={'Additional\nFees'} tip={COLUMN_TIPS.extra} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label={'Total\nfee'} tip={COLUMN_TIPS.fee} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="VAT" tip={COLUMN_TIPS.vat} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Booking" tip={COLUMN_TIPS.booking} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Payout TX" tip={COLUMN_TIPS.payout} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Discrepancy" tip={COLUMN_TIPS.discrepancy} />
                  </TableCell>
                  <TableCell align="center">
                    <HeaderTip variant="column" label="Actions" tip={COLUMN_TIPS.actions} />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.stays.map((stay) => {
                  const party = parseGuestParty(stay.guest_name, stay.platform);
                  const counts = stayPartyCounts(party);
                  const diffs = stayLinkDiscrepancies(stay, party, detail.month);
                  return (
                  <TableRow
                    key={stay.id}
                    hover
                    selected={detailStayId === stay.id}
                    onClick={() => setDetailStayId(stay.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} title={`Stay ${stay.id}`}>
                      {stay.id}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} title={formatStayDates(stay.dates_raw, stay.check_in_date, stay.check_out_date, detail.month)}>
                      {formatStayDates(stay.dates_raw, stay.check_in_date, stay.check_out_date, detail.month)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap
                        title={party.name || undefined}
                        sx={fitTruncateSx}
                      >
                        {party.name || '—'}
                      </Typography>
                      {stay.notes ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          title={stay.notes}
                          display="block"
                          sx={fitTruncateSx}
                        >
                          {stay.notes}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} title={formatGuestCounts(counts)}>
                      {formatGuestCounts(counts)}
                    </TableCell>
                    <TableCell>
                      {stay.platform ? (
                        <Chip
                          size="small"
                          label={stay.platform}
                          color={stay.platform === 'Airbnb' ? 'primary' : 'secondary'}
                          title={stay.platform}
                          sx={{ height: 22, maxWidth: '100%', '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">{money(stay.total_payment)}</TableCell>
                    <TableCell align="right">{money(stay.cleaning_fee)}</TableCell>
                    <TableCell align="right">{money(stay.total_minus_cleaning)}</TableCell>
                    <TableCell align="right">{money(stay.commission)}</TableCell>
                    <TableCell align="right" title={stay.notes || undefined}>
                      {stay.total_fee_extra && Number(stay.total_fee_extra) !== 0
                        ? money(stay.total_fee_extra)
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{money(stay.total_fee)}</TableCell>
                    <TableCell align="right">{money(stay.vat)}</TableCell>
                    <TableCell
                      className="fit-table-no-clip"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renderLinkChip('Booking', stay.booking_link_status, stay.unified_booking_id, {
                        onConfirm: stay.unified_booking_id
                          ? () =>
                              patchStayLink(stay.id, 'booking', {
                                unified_booking_id: stay.unified_booking_id,
                                status: 'confirmed',
                              })
                          : undefined,
                        onDecline: () => patchStayLink(stay.id, 'booking', { status: 'declined' }),
                        onPick: () => {
                          setLinkDialog({ kind: 'booking', stayId: stay.id });
                          setLinkId(stay.unified_booking_id ? String(stay.unified_booking_id) : '');
                        },
                      }, { hideKind: true })}
                    </TableCell>
                    <TableCell
                      className="fit-table-no-clip"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {renderLinkChip('Payout TX', stay.payout_tx_link_status, stay.payout_tx_id, {
                        onConfirm: stay.payout_tx_id
                          ? () =>
                              patchStayLink(stay.id, 'payout', {
                                payout_tx_id: stay.payout_tx_id,
                                status: 'confirmed',
                              })
                          : undefined,
                        onDecline: () => patchStayLink(stay.id, 'payout', { status: 'declined' }),
                        onPick: () => {
                          setLinkDialog({ kind: 'payout', stayId: stay.id });
                          setLinkId(stay.payout_tx_id ? String(stay.payout_tx_id) : '');
                        },
                      }, {
                        hideKind: true,
                        chipLabel:
                          payoutShareIsBalanced(stay.payout_share) && stay.payout_tx_id
                            ? `Shared #${stay.payout_tx_id}`
                            : undefined,
                        chipTitle: payoutShareIsBalanced(stay.payout_share) && stay.payout_share
                          ? payoutShareTooltip(stay.payout_share)
                          : undefined,
                      })}
                    </TableCell>
                    <TableCell
                      className="fit-table-no-clip"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {diffs.length ? (
                        <Tooltip
                          title={
                            <Box sx={{ whiteSpace: 'pre-line' }}>
                              {diffs.map((d) => `${d.field}: ${d.detail}`).join('\n')}
                            </Box>
                          }
                        >
                          <Chip
                            size="small"
                            color="warning"
                            label={diffs.map((d) => d.field).join(', ')}
                            sx={{
                              height: 22,
                              maxWidth: '100%',
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="center" className="fit-table-no-clip">
                      <Tooltip title="Open stay details">
                        <IconButton
                          size="small"
                          aria-label="Open stay details"
                          onClick={() => setDetailStayId(stay.id)}
                          sx={{ p: 0.25, width: 22, height: 22 }}
                        >
                          <VisibilityIcon sx={{ fontSize: '1.05rem' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {busyStatus ? (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 2 }}
          role="status"
          aria-live="polite"
        >
          {busyStatus}
        </Alert>
      ) : null}
      {busy ? <LinearProgress sx={{ mb: 2, borderRadius: 1 }} aria-label={busyStatus || 'Working'} /> : null}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UnfoldMoreIcon />}
          onClick={openAllSections}
          disabled={months.length === 0}
          aria-label="Open all sections"
        >
          Open all sections
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UnfoldLessIcon />}
          onClick={closeAllSections}
          disabled={months.length === 0}
          aria-label="Close all sections"
        >
          Close all sections
        </Button>
        {canWrite && (
          <>
            <Button
              variant="outlined"
              startIcon={
                busyKind === 'import' ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />
              }
              onClick={handleImport}
              disabled={busy}
              title="Imports the workbook, then suggests booking and payout TX links"
            >
              {busyKind === 'import' ? 'Importing…' : 'Import workbook'}
            </Button>
            <Button
              variant="contained"
              startIcon={
                busyKind === 'suggest' ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />
              }
              onClick={handleSuggest}
              disabled={busy}
              aria-busy={busyKind === 'suggest'}
            >
              {busyKind === 'suggest' ? 'Suggesting links…' : 'Suggest links'}
            </Button>
          </>
        )}
      </Box>

      {months.length === 0 && !error && (
        <Typography color="text.secondary">
          No Niall’s Calcs data yet. {canWrite ? 'Import the invoice workbook to get started.' : ''}
        </Typography>
      )}

      {months.map((detail) => {
        const stayCount = detail.stay_count ?? (detail.properties || []).reduce((n, g) => n + g.stays.length, 0);
        const open = isExpanded(detail.id);
        return (
          <Paper key={detail.id} variant="outlined" sx={{ mb: 1, overflow: 'visible' }}>
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={open}
              aria-controls={`niall-month-${detail.id}-content`}
              onClick={() => setMonthExpanded(detail.id, !open)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setMonthExpanded(detail.id, !open);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'baseline', flex: 1, pr: 1 }}>
                <Typography fontWeight={600}>{detail.sheet_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {money(detail.total_incl_vat)} incl VAT
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stayCount} stay{stayCount === 1 ? '' : 's'}
                </Typography>
              </Box>
              <ExpandMoreIcon
                sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
              />
            </Box>
            {open ? (
              <Box id={`niall-month-${detail.id}-content`} sx={{ px: 2, pb: 2, pt: 0.5 }}>
                {renderMonthBody(detail)}
              </Box>
            ) : null}
          </Paper>
        );
      })}

      <Drawer
        anchor="right"
        open={Boolean(detailContext)}
        onClose={() => setDetailStayId(null)}
        hideBackdrop
        ModalProps={{
          keepMounted: false,
          disableScrollLock: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
          sx: {
            zIndex: (theme) => theme.zIndex.snackbar + 15,
            pointerEvents: 'none',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: DETAIL_PANEL_WIDTH_PX },
            maxWidth: '100vw',
            zIndex: 1,
            pointerEvents: 'auto',
            borderLeft: 1,
            borderColor: 'divider',
            boxShadow: 12,
          },
        }}
      >
        {detailContext ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {(() => {
              const { stay, month, propertyCode } = detailContext;
              const party = parseGuestParty(stay.guest_name, stay.platform);
              const counts = stayPartyCounts(party);
              const diffs = stayLinkDiscrepancies(stay, party, month.month);
              const booking = stay.booking;
              const payout = stay.payout_tx;
              const formulaEntries = Object.entries(stay.excel_formulas || {}).filter(([, v]) => v);
              const calcEntries = Object.entries(stay.calc_display || {}).filter(([, v]) => v);
              return (
                <>
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 2,
                      py: 1.5,
                      bgcolor: 'background.paper',
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                        <Typography variant="h6" component="div" sx={{ lineHeight: 1.25 }}>
                          {party.name || stay.guest_name || 'Stay'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          #{stay.id} · {propertyCode} · {formatStayDates(stay.dates_raw, stay.check_in_date, stay.check_out_date, month.month)}
                        </Typography>
                      </Box>
                      <Tooltip title="Close panel">
                        <IconButton aria-label="Close stay detail panel" edge="end" onClick={() => setDetailStayId(null)}>
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Niall invoice
                        </Typography>
                        <DetailLine label="UID" value={stay.id} />
                        <DetailLine label="Month" value={month.sheet_name} />
                        <DetailLine label="Property" value={propertyCode} />
                        <DetailLine
                          label="Dates"
                          value={formatStayDates(stay.dates_raw, stay.check_in_date, stay.check_out_date, month.month)}
                        />
                        <DetailLine label="Check-in" value={stay.check_in_date} />
                        <DetailLine label="Check-out" value={stay.check_out_date} />
                        <DetailLine label="Guest" value={party.name || stay.guest_name} />
                        <DetailLine label="No. of guests" value={formatGuestCounts(counts)} />
                        <DetailLine label="Platform" value={stay.platform} />
                        <DetailLine label="Notes" value={stay.notes} />
                        <DetailLine label="Total payment" value={money(stay.total_payment)} />
                        <DetailLine label="Cleaning" value={money(stay.cleaning_fee)} />
                        <DetailLine label="Minus cleaning" value={money(stay.total_minus_cleaning)} />
                        <DetailLine label="Commission" value={money(stay.commission)} />
                        <DetailLine label="Additional fees" value={money(stay.total_fee_extra)} />
                        <DetailLine label="Total fee" value={money(stay.total_fee)} />
                        <DetailLine label="VAT" value={money(stay.vat)} />
                      </Box>
                      {(calcEntries.length > 0 || formulaEntries.length > 0) && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Formulas
                          </Typography>
                          {calcEntries.map(([key, value]) => (
                            <DetailLine key={`calc-${key}`} label={key.replace(/_/g, ' ')} value={value} />
                          ))}
                          {formulaEntries.map(([key, value]) => (
                            <DetailLine key={`xl-${key}`} label={`${key.replace(/_/g, ' ')} (Excel)`} value={value} />
                          ))}
                        </Box>
                      )}
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Discrepancy
                        </Typography>
                        {diffs.length ? (
                          diffs.map((d) => <DetailLine key={d.field} label={d.field} value={d.detail} />)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No differences from linked data.
                          </Typography>
                        )}
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Linked booking
                        </Typography>
                        <DetailLine label="Status" value={stay.booking_link_status} />
                        <DetailLine
                          label="Booking id"
                          value={
                            stay.unified_booking_id ? (
                              <Box
                                component="a"
                                href={`/bookings/${stay.unified_booking_id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                #{stay.unified_booking_id}
                              </Box>
                            ) : (
                              '—'
                            )
                          }
                        />
                        {booking ? (
                          <>
                            <DetailLine label="Guest" value={booking.guest_name} />
                            <DetailLine label="Platform" value={booking.platform} />
                            <DetailLine label="Source" value={booking.source} />
                            <DetailLine label="Check-in" value={booking.check_in_date} />
                            <DetailLine label="Check-out" value={booking.check_out_date} />
                            <DetailLine
                              label="Guests"
                              value={formatGuestCounts({
                                adults: booking.adults_count ?? null,
                                children: booking.children_count ?? null,
                              })}
                            />
                            <DetailLine label="Host payout" value={money(booking.host_payout_amount)} />
                            <DetailLine label="Total payout" value={money(booking.total_payout_amount)} />
                            <DetailLine label="Booking payout TX" value={booking.revolut_transaction_db_id ? `#${booking.revolut_transaction_db_id}` : '—'} />
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No booking linked.
                          </Typography>
                        )}
                        {renderPanelLinkActions(
                          stay.booking_link_status,
                          stay.unified_booking_id,
                          {
                            confirm: 'Confirm booking',
                            reject: 'Reject booking',
                            pick: 'Pick booking',
                          },
                          {
                            onConfirm: stay.unified_booking_id
                              ? () =>
                                  applyStayLink(stay.id, 'booking', {
                                    unified_booking_id: stay.unified_booking_id,
                                    status: 'confirmed',
                                  })
                              : undefined,
                            onDecline: () => applyStayLink(stay.id, 'booking', { status: 'declined' }),
                            onPick: () => {
                              setLinkDialog({ kind: 'booking', stayId: stay.id });
                              setLinkId(stay.unified_booking_id ? String(stay.unified_booking_id) : '');
                            },
                          }
                        )}
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Linked payout TX
                        </Typography>
                        <DetailLine label="Status" value={stay.payout_tx_link_status} />
                        <DetailLine label="TX id" value={stay.payout_tx_id ? `#${stay.payout_tx_id}` : '—'} />
                        {payout ? (
                          <>
                            <DetailLine label="Revolut id" value={payout.revolut_id} />
                            <DetailLine
                              label="Amount"
                              value={payout.amount == null ? '—' : `${money(Math.abs(Number(payout.amount)))} ${payout.currency || ''}`.trim()}
                            />
                            <DetailLine label="Description" value={payout.description} />
                            <DetailLine label="Counterparty" value={payout.counterparty_name} />
                            <DetailLine label="Completed" value={formatIsoStamp(payout.completed_at)} />
                            <DetailLine label="Created" value={formatIsoStamp(payout.created_at_revolut)} />
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No payout TX linked.
                          </Typography>
                        )}
                        {payoutShareIsBalanced(stay.payout_share) && stay.payout_share ? (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                              Bookings in this payout
                            </Typography>
                            {stay.payout_share.members.map((member) => (
                              <Box
                                key={member.booking_id}
                                sx={{
                                  mb: 1,
                                  pl: 1,
                                  borderLeft: 2,
                                  borderColor: member.is_current ? 'primary.main' : 'divider',
                                }}
                              >
                                {member.is_current ? (
                                  <Chip
                                    size="small"
                                    label="This stay"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 18, mb: 0.5, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }}
                                  />
                                ) : null}
                                <DetailLine label="Guest" value={member.guest_name} />
                                <DetailLine label="Property" value={member.property_code} />
                                <DetailLine
                                  label="Dates"
                                  value={formatStayDates(null, member.check_in_date, member.check_out_date, month.month)}
                                />
                                <DetailLine label="Platform" value={member.platform} />
                                <DetailLine
                                  label="Booking id"
                                  value={member.booking_id ? `#${member.booking_id}` : '—'}
                                />
                                <DetailLine
                                  label="Niall UID"
                                  value={member.stay_id ? `#${member.stay_id}` : '—'}
                                />
                                <DetailLine label="Payout" value={money(member.amount)} />
                              </Box>
                            ))}
                            <DetailLine label="Total" value={payoutShareTotalLine(stay.payout_share)} />
                          </Box>
                        ) : null}
                        {renderPanelLinkActions(
                          stay.payout_tx_link_status,
                          stay.payout_tx_id,
                          {
                            confirm: 'Confirm payout TX',
                            reject: 'Reject payout TX',
                            pick: 'Pick payout TX',
                          },
                          {
                            onConfirm: stay.payout_tx_id
                              ? () =>
                                  applyStayLink(stay.id, 'payout', {
                                    payout_tx_id: stay.payout_tx_id,
                                    status: 'confirmed',
                                  })
                              : undefined,
                            onDecline: () => applyStayLink(stay.id, 'payout', { status: 'declined' }),
                            onPick: () => {
                              setLinkDialog({ kind: 'payout', stayId: stay.id });
                              setLinkId(stay.payout_tx_id ? String(stay.payout_tx_id) : '');
                            },
                          }
                        )}
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Month fee TX
                        </Typography>
                        <DetailLine label="Status" value={month.fee_tx_link_status} />
                        <DetailLine label="TX id" value={month.fee_tx_id ? `#${month.fee_tx_id}` : '—'} />
                        <DetailLine label="Total incl VAT" value={money(month.total_incl_vat)} />
                        {renderPanelLinkActions(
                          month.fee_tx_link_status || 'unmatched',
                          month.fee_tx_id,
                          {
                            confirm: 'Confirm fee TX',
                            reject: 'Reject fee TX',
                            pick: 'Pick fee TX',
                          },
                          {
                            onConfirm: month.fee_tx_id
                              ? () =>
                                  applyFeeLink(month.id, {
                                    fee_tx_id: month.fee_tx_id,
                                    status: 'confirmed',
                                  })
                              : undefined,
                            onDecline: () => applyFeeLink(month.id, { status: 'declined' }),
                            onPick: () => {
                              setLinkDialog({ kind: 'fee', monthId: month.id });
                              setLinkId(month.fee_tx_id ? String(month.fee_tx_id) : '');
                            },
                          }
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </>
              );
            })()}
          </Box>
        ) : null}
      </Drawer>

      <Dialog open={Boolean(linkDialog)} onClose={() => setLinkDialog(null)}>
        <DialogTitle>
          {linkDialog?.kind === 'booking'
            ? 'Link booking'
            : linkDialog?.kind === 'payout'
              ? 'Link payout TX'
              : 'Link monthly fee TX'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="normal"
            fullWidth
            label={linkDialog?.kind === 'booking' ? 'unified_bookings id' : 'revolut_transactions id'}
            value={linkId}
            onChange={(e) => setLinkId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitManualLink} disabled={busy}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
